import { ApiErrorCode } from "config/errorCodes";

export type AuthErrorMethod = "password" | "totp" | "google" | "linkedin" | "sso" | "signup";

/** A fixed list so sign-in failures can be grouped in PostHog. */
export type AuthErrorReason =
  | "invalid_credentials"
  | "sso_required"
  | "sso_not_entitled"
  | "social_conflict"
  | "locked_out"
  | "account_deactivated"
  | "pending_deletion"
  | "totp_invalid"
  | "totp_session_expired"
  | "email_taken"
  | "weak_password"
  | "captcha_failed"
  | "forbidden"
  | "rate_limited"
  | "network"
  | "server"
  | "other";

type ErrorLike = {
  status_code?: unknown;
  code?: unknown;
  message?: unknown;
  response?: { status?: unknown; data?: { code?: unknown; message?: unknown } };
};

const asNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const readStatus = (error: ErrorLike): number | undefined =>
  asNumber(error.status_code) ?? asNumber(error.response?.status);

const readCode = (error: ErrorLike): string | undefined =>
  asString(error.response?.data?.code) ?? asString(error.code);

const readMessage = (error: unknown): string => {
  if (!error || typeof error !== "object") return "";
  const e = error as ErrorLike;
  return asString(e.response?.data?.message) ?? asString(e.message) ?? "";
};

export const resolveAuthErrorReason = (error: unknown): AuthErrorReason => {
  if (!error || typeof error !== "object") return "other";
  const e = error as ErrorLike;
  const status = readStatus(e);
  const code = readCode(e);

  switch (code) {
    case ApiErrorCode.SSO_REQUIRED_FOR_DOMAIN:
      return "sso_required";
    case "SSO_NOT_ENTITLED":
      return "sso_not_entitled";
    case ApiErrorCode.INVALID_LOGIN_METHOD:
      return "social_conflict";
    case ApiErrorCode.AUTH_TURNSTILE_VERIFICATION_FAILED:
      return "captcha_failed";
    case ApiErrorCode.RATE_LIMITED:
      return "rate_limited";
    case ApiErrorCode.TEMPORARY_LOCKOUT:
      return "locked_out";
    case ApiErrorCode.ACCOUNT_DEACTIVATED:
      return "account_deactivated";
    case ApiErrorCode.ACCOUNT_PENDING_DELETION:
      return "pending_deletion";
    case "INVALID_TOTP_TOKEN":
      return "totp_invalid";
    case "SESSION_EXPIRED":
      return "totp_session_expired";
    case "INVALID_EMAIL_OR_PASSWORD":
      return "invalid_credentials";
    default:
      break;
  }

  if (code?.includes("ALREADY_EXISTS")) return "email_taken";
  if (code?.startsWith("PASSWORD_TOO")) return "weak_password";
  if (status === 429) return "rate_limited";
  if (status === 409) return "social_conflict";
  if (status === 401) return "invalid_credentials";
  if (status === 403) return "forbidden";
  if (status !== undefined && status >= 500) return "server";
  if (status === undefined || status === 0) {
    const message = readMessage(error).toLowerCase();
    if (/network|failed to fetch|timeout|load failed/.test(message)) return "network";
  }
  return "other";
};

/** Sign-up failed because an account with this email already exists. */
export const isEmailTakenError = (error: unknown): boolean =>
  resolveAuthErrorReason(error) === "email_taken";

/**
 * Properties for `sign_in_error` and `sign_up_error`. No email or name:
 * these go to PostHog and the GTM dataLayer.
 */
export const buildAuthErrorProperties = (method: AuthErrorMethod, error: unknown) => {
  const e = (error && typeof error === "object" ? error : {}) as ErrorLike;
  return {
    method,
    reason: resolveAuthErrorReason(error),
    http_status: readStatus(e) ?? 0,
    error_code: readCode(e) ?? null,
    error: readMessage(error) || null,
  };
};
